import React from 'react';
import { Box, TextField, Typography, RadioGroup, FormControlLabel, Radio, Paper, Switch } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

const sectionPaperSx = {
  mb: 3, p: 2,
  border: '2px solid',
  borderColor: 'divider',
  borderRadius: 2,
  background: 'background.default',
} as const;

const sectionTitleSx = {
  fontWeight: 700, mb: 1.5,
  color: (theme: any) => theme.palette.primary.main,
} as const;

interface UnplugPlugSettingsProps {
  sourcePdb: string;
  setSourcePdb: (v: string) => void;
  targetCdb: string;
  setTargetCdb: (v: string) => void;
  fileNameConvertOption: string;
  setFileNameConvertOption: (v: string) => void;
  sourcePath: string;
  setSourcePath: (v: string) => void;
  targetPath: string;
  setTargetPath: (v: string) => void;
  manageStandbysClause: string;
  setManageStandbysClause: (v: string) => void;
  platform: string;
  sid: string;
}

const tooltipSx = (theme: any) => ({
  bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
  color: theme.palette.mode === 'dark' ? '#222' : '#fff',
  fontSize: 15,
  borderRadius: 2,
  boxShadow: 3,
  px: 2,
  py: 1,
  fontWeight: 400,
  minWidth: 320,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getRadio = (v: string): string => {
  if (!v || v === 'standbys=none') return 'none';
  if (v === 'standbys=all') return 'all';
  if (v.startsWith('standbys=all except')) return 'allexcept';
  if (/^standbys=\(/.test(v)) return 'specific';
  return 'none';
};

const extractNames = (v: string, pattern: RegExp): string => {
  const m = v.match(pattern);
  if (!m) return '';
  return m[1].replace(/'/g, '').replace(/,\s*/g, ', ').trim();
};

const buildAllExcept = (names: string): string => {
  const parts = names.split(',').map(s => s.trim()).filter(Boolean);
  return `standbys=all except (${parts.map(p => `'${p}'`).join(',')})`;
};

const buildSpecific = (names: string): string => {
  const parts = names.split(',').map(s => s.trim()).filter(Boolean);
  return `standbys=(${parts.map(p => `'${p}'`).join(',')})`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const UnplugPlugSettings: React.FC<UnplugPlugSettingsProps> = ({
  sourcePdb, setSourcePdb,
  targetCdb, setTargetCdb,
  fileNameConvertOption, setFileNameConvertOption,
  sourcePath, setSourcePath,
  targetPath, setTargetPath,
  manageStandbysClause, setManageStandbysClause,
  platform,
  sid,
}) => {
  const isWindows = platform === 'WINDOWS.X64';

  const enabledRecovery = !!manageStandbysClause;
  const radio = getRadio(manageStandbysClause);

  const [allExceptNames, setAllExceptNames] = React.useState<string>(
    radio === 'allexcept' ? extractNames(manageStandbysClause, /standbys=all except \((.+)\)/i) : ''
  );
  const [specificNames, setSpecificNames] = React.useState<string>(
    radio === 'specific' ? extractNames(manageStandbysClause, /standbys=\((.+)\)/) : ''
  );

  // Default to NONE when entering Unplug-Plug settings
  React.useEffect(() => {
    if (!fileNameConvertOption || fileNameConvertOption === 'nocopy' || fileNameConvertOption === 'convert') {
      setFileNameConvertOption('none');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleRecovery = (enabled: boolean) => {
    if (!enabled) {
      setManageStandbysClause('');
    } else {
      setManageStandbysClause('standbys=none');
    }
  };

  const handleRadioChange = (v: string) => {
    if (v === 'none')           setManageStandbysClause('standbys=none');
    else if (v === 'all')       setManageStandbysClause('standbys=all');
    else if (v === 'allexcept') setManageStandbysClause(buildAllExcept(allExceptNames));
    else if (v === 'specific')  setManageStandbysClause(buildSpecific(specificNames));
  };

  return (
    <Paper elevation={0} sx={{ p: 3, background: 'background.paper', borderRadius: 3 }}>

      {/* PDB / CDB */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>PDB / CDB</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Source PDB:</Typography>
          <TextField fullWidth size="small" value={sourcePdb}
            onChange={e => setSourcePdb(e.target.value)}
            placeholder={isWindows ? 'PDB1_SRC' : 'pdb1_src'} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Target CDB:</Typography>
          <TextField fullWidth size="small" value={targetCdb}
            onChange={e => setTargetCdb(e.target.value)}
            placeholder={isWindows ? 'CDB19_TGT' : 'cdb19_tgt'} />
        </Box>
      </Paper>

      {/* File Name Convert / Copy Option */}
      <Paper elevation={0} sx={sectionPaperSx}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>File Name Convert / Copy Option</Typography>
        <RadioGroup
          value={fileNameConvertOption || 'none'}
          onChange={e => setFileNameConvertOption(e.target.value)}
          sx={{
            '.MuiFormControlLabel-root': { mb: 1.5, alignItems: 'center', display: 'flex' },
            '.MuiSvgIcon-root': { fontSize: 20, ml: 0.5, color: 'primary.main' },
          }}
        >
          <FormControlLabel value="none" control={<Radio />} label={
            <Tooltip title="Writes file_name_convert=NONE in the config. Required when setting DB_CREATE_FILE_DEST or PDB_FILE_NAME_CONVERT on the Target CDB." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
              <Typography sx={{ fontWeight: 600 }}>NONE</Typography>
            </Tooltip>
          } />
          <FormControlLabel value="convert" control={<Radio />} label={
            <Tooltip title="file_name_convert will be used directly in the AutoUpgrade config file. You cannot use this option if you have OMF files." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
              <Typography sx={{ fontWeight: 600 }}>Use Source/Target Paths</Typography>
            </Tooltip>
          } />
        </RadioGroup>

        {/* Source / Target paths */}
        {fileNameConvertOption === 'convert' && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Path:</Typography>
              <TextField fullWidth size="small" value={sourcePath}
                onChange={e => setSourcePath(e.target.value)}
                placeholder={isWindows
                  ? `${(sid || 'DB1').toUpperCase()}\\${(sourcePdb || 'PDB1_SRC').toUpperCase()}`
                  : `${sid || 'DB1'}/${sourcePdb || 'pdb1_src'}`}
                sx={{ mt: 1, mb: 2 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Path:</Typography>
              <TextField fullWidth size="small" value={targetPath}
                onChange={e => setTargetPath(e.target.value)}
                placeholder={isWindows
                  ? `${(targetCdb || 'CDB19_TGT').toUpperCase()}\\${(sourcePdb || 'PDB1_SRC').toUpperCase()}`
                  : `${targetCdb || 'cdb19_tgt'}/${sourcePdb || 'pdb1_src'}`}
                sx={{ mt: 1 }} />
            </Box>
          </>
        )}
      </Paper>

      {/* Manage Standbys Clause */}
      <Paper elevation={0} sx={{ ...sectionPaperSx, mb: 0 }}>
        <Typography variant="subtitle1" sx={sectionTitleSx}>Manage Standbys Clause</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Switch
            checked={enabledRecovery}
            onChange={e => handleToggleRecovery(e.target.checked)}
            color="primary"
            size="small"
          />
          <Typography sx={{ fontWeight: 600, ml: 1 }}>
            {enabledRecovery ? 'Enabled Recovery' : 'Deferrable Recovery'}
          </Typography>
        </Box>

        {enabledRecovery && (
          <>
            <RadioGroup
              value={radio}
              onChange={e => handleRadioChange(e.target.value)}
              sx={{
                '.MuiFormControlLabel-root': { mb: 1.5, alignItems: 'center', display: 'flex' },
                '.MuiSvgIcon-root': { fontSize: 20, ml: 0.5, color: 'primary.main' },
              }}
            >
              <FormControlLabel value="none" control={<Radio />} label={
                <Tooltip title="Writes standbys=none — no standby CDB recovers the PDB (AutoUpgrade default for Enabled Recovery)." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                  <Typography sx={{ fontWeight: 600 }}>NONE</Typography>
                </Tooltip>
              } />
              <FormControlLabel value="all" control={<Radio />} label={
                <Tooltip title="PDB is recovered on all standby CDBs." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                  <Typography sx={{ fontWeight: 600 }}>ALL</Typography>
                </Tooltip>
              } />
              <FormControlLabel value="allexcept" control={<Radio />} label={
                <Tooltip title="PDB is recovered on all standby CDBs except the ones listed." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                  <Typography sx={{ fontWeight: 600 }}>ALL EXCEPT</Typography>
                </Tooltip>
              } />
              <FormControlLabel value="specific" control={<Radio />} label={
                <Tooltip title="PDB is recovered only on the specified standby CDBs. All other standbys will not recover the PDB." placement="right" arrow componentsProps={{ tooltip: { sx: tooltipSx } }}>
                  <Typography sx={{ fontWeight: 600 }}>Specific PDBs</Typography>
                </Tooltip>
              } />
            </RadioGroup>

            {/* ALL EXCEPT input */}
            {radio === 'allexcept' && (
              <TextField fullWidth size="small" value={allExceptNames}
                onChange={e => {
                  const v = e.target.value;
                  setAllExceptNames(v);
                  setManageStandbysClause(buildAllExcept(v));
                }}
                placeholder="e.g. cdb3_stby_1, cdb3_stby_2"
                label="Standby PDB names (comma-separated)"
                sx={{ mt: 0.5, mb: 1 }} />
            )}

            {/* Specific Standbys input */}
            {radio === 'specific' && (
              <TextField fullWidth size="small" value={specificNames}
                onChange={e => {
                  const v = e.target.value;
                  setSpecificNames(v);
                  setManageStandbysClause(buildSpecific(v));
                }}
                placeholder="e.g. cdb4_stby_1, cdb4_stby_2"
                label="Standby PDB names (comma-separated)"
                sx={{ mt: 0.5, mb: 1 }} />
            )}
          </>
        )}
      </Paper>
    </Paper>
  );
};

export default UnplugPlugSettings;
